import { ApiError } from '@api/client'
import { useI18n } from '@i18n/I18nProvider'
import type { ReactElement } from 'react'
import { Button } from './Button'
import { Icon } from './Icon'
import { Skeleton } from './Skeleton'

// Qué forma tiene lo que se está cargando: una ficha (foto, título y texto), una lista de filas,
// una rejilla de fotos, unas tarjetas de cifras o el mapa entero.
export type LoadingShape = 'detail' | 'list' | 'grid' | 'cards' | 'map'

const Row = () => (
	<div className="flex items-center gap-3 rounded-3xl bg-white p-4 shadow-sm">
		<Skeleton className="size-16 shrink-0" />
		<div className="flex min-w-0 flex-1 flex-col gap-2">
			<Skeleton className="h-5 w-3/5" />
			<Skeleton className="h-4 w-4/5" />
		</div>
	</div>
)

const SHAPES: Record<LoadingShape, () => ReactElement> = {
	detail: () => (
		<div className="flex flex-col gap-3">
			<Skeleton className="aspect-[4/3] w-full rounded-3xl" />
			<Skeleton className="mt-2 h-4 w-1/4" />
			<Skeleton className="h-8 w-3/4" />
			<Skeleton className="h-5 w-1/2" />
			<Skeleton className="mt-3 h-4 w-full" />
			<Skeleton className="h-4 w-11/12" />
			<Skeleton className="h-4 w-4/5" />
		</div>
	),
	list: () => (
		<div className="flex flex-col gap-3">
			<Row />
			<Row />
			<Row />
			<Row />
		</div>
	),
	grid: () => (
		<div className="grid grid-cols-2 gap-3">
			{['a', 'b', 'c', 'd'].map((key) => (
				<div key={key} className="overflow-hidden rounded-3xl bg-white shadow-sm">
					<Skeleton className="aspect-square w-full rounded-none" />
					<div className="p-3">
						<Skeleton className="h-5 w-3/4" />
					</div>
				</div>
			))}
		</div>
	),
	cards: () => (
		<div className="grid grid-cols-2 gap-3">
			{['a', 'b', 'c', 'd'].map((key) => (
				<div key={key} className="flex flex-col gap-2 rounded-3xl bg-white p-4 shadow-sm">
					<Skeleton className="h-8 w-1/2" />
					<Skeleton className="h-4 w-3/4" />
				</div>
			))}
		</div>
	),
	map: () => <Skeleton className="size-full rounded-none" />,
}

// Mientras cargan los datos: el esqueleto de lo que va a salir (en vez de una ruedecita) y, para
// los lectores de pantalla, "Cargando…".
export const LoadingState = ({ shape = 'detail' }: { shape?: LoadingShape }) => {
	const { t } = useI18n()
	const Shape = SHAPES[shape]
	return (
		<div role="status" data-shape={shape} className={shape === 'map' ? 'size-full' : 'py-4'}>
			<span className="sr-only">{t('common.loading')}</span>
			<Shape />
		</div>
	)
}

// Error de carga: distingue "sin cobertura" (lo habitual en el Camino) de un fallo de la API.
export const ErrorState = ({ error, onRetry }: { error: unknown; onRetry?: () => void }) => {
	const { t } = useI18n()
	const offline = error instanceof ApiError && error.offline
	return (
		<div role="alert" className="flex flex-col items-center gap-4 px-4 py-12 text-center">
			<Icon name="alert" className="size-10 text-dry" />
			<p className="text-lg">{offline ? t('common.offline') : t('common.error')}</p>
			{onRetry && (
				<Button variant="secondary" onClick={onRetry}>
					{t('common.retry')}
				</Button>
			)}
		</div>
	)
}
